export type DocStatus = "draft" | "review" | "published" | "superseded";

export const DOC_STATUSES: DocStatus[] = [
  "draft",
  "review",
  "published",
  "superseded",
];

/**
 * Website Sanity content types a document can be published as. Single source of
 * truth shared by the editor (doc-type tag), the publish page (target select),
 * and the worker (publish routing).
 */
export type SanityTargetType = "methodology" | "factsheet" | "pdfDocument";

export const SANITY_TARGET_TYPES: SanityTargetType[] = [
  "methodology",
  "factsheet",
  "pdfDocument",
];

export function isDocStatus(val: unknown): val is DocStatus {
  return typeof val === "string" && DOC_STATUSES.includes(val as DocStatus);
}

/**
 * Map a raw front-matter status onto the canonical shelf vocabulary. Source
 * docs (e.g. the indexer-rust repo) sometimes use aliases like "publish";
 * anything unrecognized falls back to 'draft' so it can never be mistaken for
 * published. Apply at every boundary where git front matter is read.
 */
const STATUS_ALIASES: Record<string, DocStatus> = {
  draft: "draft",
  review: "review",
  publish: "published",
  published: "published",
  superseded: "superseded",
};

export function normalizeDocStatus(val: unknown): DocStatus {
  if (typeof val !== "string") return "draft";
  return STATUS_ALIASES[val.trim().toLowerCase()] ?? "draft";
}

/** Only documents in review may enter the publish flow. */
export function canPublish(status: DocStatus): boolean {
  return status === "review";
}

/** Pre-release docs eligible for the bulk review+publish flow: drafts (which get
 *  auto-promoted to review) and docs already in review. */
export function isReleaseEligible(status: DocStatus): boolean {
  return status === "draft" || status === "review";
}

/**
 * Forward transitions allowed from each status. Staying in the current
 * status is always allowed and is not listed here.
 */
export const allowedTransitions: Record<DocStatus, DocStatus[]> = {
  draft: ["review"],
  review: ["draft", "published"],
  published: ["superseded"],
  superseded: [],
};

export function canTransition(from: DocStatus, to: DocStatus): boolean {
  return from === to || allowedTransitions[from].includes(to);
}
